import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { differenceInMilliseconds } from 'date-fns'
import { debounce } from 'lodash'
import { isTestEnv } from 'constants/Utils.constants'
import qs from 'qs'
import produce from 'immer'
import { useLocation, useNavigate } from 'react-router-dom'
import ms from 'ms'

export function useId() {
  return useRef(uuidv4()).current
}

export function useWindowFocused() {
  const [isFocused, setFocused] = useState<boolean>(true)

  useEffect(() => {
    function handleFocus(isFocused: boolean) {
      setFocused(isFocused)
    }

    window.addEventListener('focus', () => handleFocus(true))
    window.addEventListener('blur', () => handleFocus(false))

    return () => {
      window.removeEventListener('focus', () => handleFocus(true))
      window.removeEventListener('blur', () => handleFocus(false))
    }
  }, [])

  return isFocused
}

// Helps create a function that can be passed to an event handler and guarantees that
// the registered callback will have the most up-to-date state
export function useEvent<Handler extends (...args: any[]) => any | Promise<any>>(handler: Handler) {
  const handlerRef = useRef(handler)
  useLayoutEffect(() => {
    handlerRef.current = handler
  })

  return useCallback((...args: Parameters<typeof handler>): ReturnType<typeof handler> => {
    const fn = handlerRef.current
    return fn(...args)
  }, [])
}

export function useWindowDimensions() {
  const [dimensions, setDimension] = useState({
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
  })
  const handleResize = useEvent(() => {
    setDimension({
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      outerWidth: window.outerWidth,
      outerHeight: window.outerHeight,
    })
  })
  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return dimensions
}

/**
 * This function will detect when you call a function too many times within a short period of time.
 * Once it detects that the call threshold has been reached, then it will enter debounce mode and aggregate
 * all your invocations for some debounce period, then invoke an agreggate function.
 */
export function useCallAggregator<
  Fn extends (...args: any[]) => void | Promise<void>,
  AggregatorFn extends (...args: any[]) => void | Promise<void>
>({
  thresholdCount = 2,
  thresholdTime = ms('3s'),
  debounceTime = isTestEnv ? 100 : ms('5s'),
  fn,
  aggregatorFn,
}: {
  thresholdCount?: number
  thresholdTime?: number
  debounceTime?: number
  fn: Fn
  aggregatorFn: AggregatorFn
}): [(...args: Parameters<typeof fn>) => void, (...args: Parameters<typeof fn>) => Promise<void>] {
  const state = useRef<{ count: number; time: number; debounceMode: boolean; calls: Parameters<typeof fn>[] }>({
    count: 0,
    time: 0,
    debounceMode: false,
    calls: [],
  })

  const debouncedFn = useCallback(
    debounce(() => {
      const calls = [...state.current.calls]
      state.current = { count: 0, time: 0, debounceMode: false, calls: [] }
      void aggregatorFn(calls)
    }, debounceTime),
    []
  )

  const debouncedFnAsync = useCallback(
    debounce(async () => {
      const calls = [...state.current.calls]
      state.current = { count: 0, time: 0, debounceMode: false, calls: [] }
      await aggregatorFn(calls)
    }, debounceTime),
    []
  )

  function performStateUpdates() {
    const now = new Date().getTime()
    const timeSinceStart = differenceInMilliseconds(now, state.current.time)
    const isWithinTimeThreshold = timeSinceStart <= thresholdTime
    const isPastCountThreshhold = state.current.count >= thresholdCount

    if (!state.current.time) {
      state.current.time = now
    }

    if (isWithinTimeThreshold) {
      if (isPastCountThreshhold) {
        state.current.debounceMode = true
      }
    } else {
      state.current.time = now
      state.current.count = 1
    }
  }

  const callHandler = useCallback(
    (...args: Parameters<typeof fn>) => {
      state.current.count++

      if (state.current.debounceMode) {
        state.current.calls.push(args)
        debouncedFn()
      } else {
        performStateUpdates()
        void fn(...args)
      }
    },
    [fn, aggregatorFn]
  )

  const callHandlerAsync = useCallback(
    async (...args: Parameters<typeof fn>): Promise<void> => {
      state.current.count++

      if (state.current.debounceMode) {
        state.current.calls.push(args)
        await debouncedFnAsync()
      } else {
        performStateUpdates()
        await fn(...args)
      }
    },
    [fn, aggregatorFn]
  )

  return [callHandler, callHandlerAsync]
}

export function useQueryParamRemover() {
  const location = useLocation()
  const navigate = useNavigate()

  return useCallback(
    (params: string[]) => {
      if (!params.length) return

      let queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
      params.forEach((param) => {
        queryParams = produce(queryParams, (draft) => {
          delete draft[param]
        })
      })
      navigate(
        location.pathname +
          (Object.keys(queryParams).length > 0 ? qs.stringify(queryParams, { addQueryPrefix: true }) : ''),
        { replace: true }
      )
    },
    [location.pathname, location.search, location.state, navigate]
  )
}
